import { IHttpResponse, IHttpService, IQService, IScope, ITimeoutService } from 'angular';
import moment from 'moment';
import { handleFormError } from 'assets/js/misc';
import { SelectizeSettings } from 'assets/js/SelectizePlugin-InfiniteScroll';
import { Controller } from '../shared/model/app.model';
import { ErrorMessage } from '../shared/model/properties.model';
import { ErrorHandlerService } from '../shared/services/error-handler.service';
import { ChoiceList, ChoiceListEmbedded, ChoiceListItem, ChoiceListItemEmbedded, TableState } from 'ng/src/app/choice-lists/shared/choice-list.model';
import { HALResponse } from 'ng/src/app/shared/model/app.model';

angular.module('logbook')
	.directive('choiceListEditor', ['$timeout', "$http", "$q", "errorHandler",
		($timeout: ITimeoutService, $http: IHttpService, $q: IQService, errorHandler: ErrorHandlerService) => {
			return {
				restrict: 'E',

				scope: {
					selectedLog: '=',
					choiceList: '='
				},

				template: require('./choice-list-editor.html'),

				controller: ['$scope', function(this: Controller, scope: IScope) {
					this.searchChoiceInput = "";

					scope.choicesPerPage = 15;
					scope.pageOptions = [15, 25, 50];
					scope.refreshChoiceList = () => {
						let cancelExecuted = false;

						if (scope.cancelChoiceListFetch)
							scope.cancelChoiceListFetch.resolve();

						scope.cancelChoiceListFetch = $q.defer();
						scope.cancelChoiceListFetch.promise.then(() => cancelExecuted = true);

						return $http.get<HALResponse<{ choiceLists: ChoiceList[] }>>('api/choiceLists', {
							noErrorPopup: true,
							timeout: scope.cancelChoiceListFetch.promise,
							params: {
								id: scope.choiceList.id
							}
						})
						.then(response => {
							if (response.data._embedded.choiceLists.length)
								scope.choiceList = response.data._embedded.choiceLists[0];
						})
						.catch(error => {
							if (!cancelExecuted)
								errorHandler.handle(error.errorMessage, JSON.stringify(error.config), true);
							
							return $q.reject();
						});
					};

					/**
					 * Reloads the current table state
					 * @param {Boolean=false} clearCells - Whether to clear the table prior to refresh
					 * @param {Boolean=false} clearNewChoiceIndex - Whether to clear the new choice insertion index
					 */
					scope.reloadTableState = (clearCells?: boolean, clearNewChoiceIndex?: boolean) => {
						if (!!clearCells)
							scope.choices = [];

						if (!!clearNewChoiceIndex)
							scope.newChoiceIndex = -1;

						if (scope.tableState) {
							return scope.refreshChoiceList().then(() => {
								let tableState: TableState = scope.tableState;

								tableState.pagination.numberOfPages = Math.ceil(scope.choiceList.numChoices / tableState.pagination.number);
								tableState.pagination.totalItemCount = scope.choiceList.numChoices;
							
								console.log(tableState);

								let start = tableState.pagination.start,
									number = tableState.pagination.number || 15;

								scope.params = {
									size: number,
									list: scope.choiceList.id,
									page: Math.ceil(start / number),
									sort: 'sortOrder'
								};

								scope.refreshChoices = () => {
									let page = scope.params.page,
										number = scope.params.size,
										startIndex = page * number,
										refreshMoment = scope.refreshMoment = moment();

									return $http.get<HALResponse<{ choiceListItems: ChoiceListItem[] }>>('api/choiceListItems', {
										params: scope.params
									})
									.then(result => {
										if (refreshMoment !== scope.refreshMoment)
											return;

										scope.choices = result.data._embedded.choiceListItems;
										scope.pages = result.data.page.totalPages;

										scope.choices.forEach((choice: ChoiceListItem, index: number) => {
											choice.index = startIndex + index;
										});

										if (scope.choices.length)
											scope.exampleChoice = scope.choices[0].value;

										scope.newChoice = {};

										if (!scope.choices.length)
											scope.insertAfter();
										else if (scope.newChoiceIndex >= 0)
											scope.insertAfter(scope.choices[scope.newChoiceIndex]);
										else
											scope.insertAfter(scope.choices[scope.choices.length - 1]);
									});
								};

								return scope.refreshChoices();
							});
						}
					};

					this.setTableState = (tableState: TableState) => {
						scope.tableState = tableState;
						
						return scope.reloadTableState(true, true);
					}
				}],
				controllerAs: 'ctrl',
				link: {
					pre: scope => {
						scope.selectizeSettings = new SelectizeSettings('choiceListItems', 'matches', 'value', 'value')
							.addUrlParameter('listId', scope.choiceList.id)
							.addUrlParameter('sort', 'sortOrder,asc')
							.addSortField('sortOrder', 'asc')
							.addCustom('maxItems', 1)
					},

					post: scope => {
						scope.showingLargeListHelp = false;
						scope.exampleChoice = '';

						scope.moveChoice = (index: number, dir: number) => {
							let choice = scope.choices[index];

							return $q(resolve => {
								if (index + dir >= scope.params.size || (dir < 0 && index - 2 < 0)) {
									let params = JSON.parse(JSON.stringify(scope.params));
									params.page += dir;
									if( params.page < 0 )
										resolve(-1)
									else
										resolve($http.get<HALResponse<{ choiceListItems: ChoiceListItem[] }>>('api/choiceListItems', {
											params: params
										})
										.then(results => {
											let choiceListItems = results.data._embedded.choiceListItems;
	
											return choiceListItems[dir == 1 ? 0 : choiceListItems.length - 2].id;
										}))
								}
								else
									resolve(scope.choices[dir == 1 ? index + dir : index - 2].id)
							})
							.then(newPrevItemId => {
								choice.prevItemId = newPrevItemId;
								return scope.updateField(choice, 'prevItemId', choice.prevItemId)
							})
							.then(scope.refreshChoices);
						};

						scope.getChoiceListCheckRequest = (listName: string) => {
							return $http.get('api/choiceLists', {
								params: {
									log: scope.selectedLog.id,
									name: listName,
									size: 1
								},
								noErrorPopup: true
							});
						};

						scope.updateField = (choiceList: ChoiceList | ChoiceListItem, field: string, $data: string) => {
							let update: { [field: string] : string } = {};
							update[field] = $data;

							return $q((resolve, reject) => {
								if (field === 'name') {
									return scope.getChoiceListCheckRequest($data)
										.then(
											(response: IHttpResponse<HALResponse<{ choiceLists: ChoiceList[] }>>) => {
												let lists = response.data._embedded.choiceLists;

												// Verify the list is not the same as the one being edited.
												if (lists.length === 1 && lists[0].id !== choiceList.id) {
													reject(`List ${$data} already exists.`)
												}
												else
													resolve();
											},
											(error: ErrorMessage) => {
												reject(error.errorMessage);
											}
										);
								}
								else
									resolve();
							})
							.then(() => {
								return $http.patch<ChoiceListEmbedded | ChoiceListItemEmbedded>(choiceList._links.self.href, update, { noErrorPopup: true })
									.then(response => {
										(choiceList as any)[field] = (response.data as any)[field];
									});
							});
						};

						scope.toggleLargeListHelp = (isShowing: boolean) => {
							scope.showingLargeListHelp = (isShowing == undefined) ? !scope.showingLargeListHelp : !!isShowing;
						};

						scope.addChoice = () => {
							scope.newChoice['list'] = scope.choiceList._links.self.href;
							let d = $q.defer();

							$http.post('api/choiceListItems', scope.newChoice, {
								noErrorPopup: true,
								rawError: true
							})
							.then(
								() => {
									scope.newChoiceIndex = scope.choices.indexOf(scope.newChoiceMarker);

									// Go to next page if the choice goes to the next page
									let pageIncrement = scope.newChoiceIndex === scope.choicesPerPage;

									if (pageIncrement)
										scope.tableState.pagination.start += scope.tableState.pagination.number;

									d.resolve(scope.reloadTableState(pageIncrement, pageIncrement));
								},
								errors => {
									handleFormError(d, scope.newChoiceForm, errors);
									d.reject();
								}
							);

							return d.promise;
						};

						scope.deleteChoice = (choice: ChoiceListItem) => {
							$http.delete(choice._links.self.href)
								.then(() => {
									if (scope.tableState.pagination.start > 0 && scope.choices.length - 2 === 0)
										scope.tableState.pagination.start -= scope.tableState.pagination.number;
									
									return scope.reloadTableState();
								});
						};

						scope.resetNewChoice = () => {
							scope.newChoice = {
								newMarker: true,
								prevItemId: scope.newChoice.prevItemId,
							};
						};

						scope.setNewChoiceField = (field: string, $data: string) => {
							scope.newChoice[field] = $data;
						};

						scope.cancelNewChoice = () => {
							scope.choices.splice(scope.choices.indexOf(scope.newChoiceMarker), 1);
							scope.choices.push(scope.newChoiceMarker);
						};

						scope.insertAfter = (choice: ChoiceListItem) => {
							if (!scope.newChoiceMarker)
								scope.newChoiceMarker = { 'newMarker': true };

							let curIndex = scope.choices.indexOf(scope.newChoiceMarker);
							if (curIndex >= 0)
								scope.choices.splice(curIndex, 1);

							scope.choices.splice(scope.choices.indexOf(choice) + 1, 0, scope.newChoiceMarker);
							scope.newChoice['prevItemId'] = choice ? choice.id : null;
						};

						scope.getAvailableChoiceMovement = (index: number, modifier: 1 | -1) => {
							if (!scope.choiceList)
								return false;

							let newIndex = index + modifier;

							return newIndex < scope.choiceList.numChoices && newIndex >= 0;
						};

						/**
						 * Toggles the search UI
						 * @param {boolean=} enabled - Defaults to toggling searchEnabled
						 */
						scope.toggleSearchUI = (enabled: boolean) => scope.searchEnabled = enabled;

						/**
						 * Switches out of search mode and sets the table to the page of the item indexed
						 * @param {Number} index
						 */
						scope.goToChoiceIndexPage = (index: number) => {
							scope.tableState.pagination.start = Math.floor((index - 1) / scope.choicesPerPage) * scope.choicesPerPage;
							scope.searchEnabled = false;
							scope.reloadTableState(true);
						};

						/**
						 * Sets the timeout for fetching search results
						 * @return {*}
						 */
						scope.setSearchTimeout = () => {
							$timeout.cancel(scope.pendingSearchTimeout);

							return scope.pendingSearchTimeout = $timeout(scope.getSearchResults, 400);
						};

						/**
						 * Retrieves search results and sets
						 * @return {Promise}
						 */
						scope.getSearchResults = () => {
							scope.noSearchResults = false;

							let reqMoment = scope.searchMoment = moment();

							if (!scope.ctrl.searchChoiceInput.length) {
								scope.searchResults = [];
								return $q(resolve => { resolve([]) });
							}

							return $http.get<HALResponse<{ choiceListItems: ChoiceListItem[] }>>('api/choiceListItems', {
								params: {
									list: scope.choiceList.id,
									matches: scope.ctrl.searchChoiceInput,
									sort: 'sortOrder'
								}
							})
							.then(results => {
								if (reqMoment !== scope.searchMoment)
									return;

								scope.searchResults = results.data._embedded.choiceListItems;

								scope.searchResults.forEach((choice: ChoiceListItem) => {
									if (choice.sortOrder === 0)
										choice.index = 1;
									else
										choice.index = choice.sortOrder / 10 + 1;
								});

								scope.noSearchResults = scope.searchResults.length === 0;

								return scope.searchResults;
							});
						}

						scope.$on('helpOpening', ($event, help) => {
							help.setSection('list-choice-editor', 8);
						})
					}
				}
			}
		}
	]);
